import React, { useState, useEffect, useRef, useMemo } from "react";
import DebtAnalysisTable from "../../shared/DebtAnalysisTable";
import { Dialog } from "@headlessui/react";
import { formatCurrency } from "../../../utils/formatters";
import HouseholdAssetAnalysis from "./HouseholdAssetAnalysis";
import HouseholdNetWorthCard from "./HouseholdNetWorthCard";
import { CalendarIcon } from "@heroicons/react/24/outline";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { getAllAssetsWithPrimaryResidence } from "../../../utils/assetHelpers";
import { useTheme as useCustomTheme } from "../../../contexts/ThemeContext";
import { CARD_BASE } from "../../../constants";

import logger from '../../../utils/logger';

// Check if framer-motion is available and only import if it is
let motion = null;
let AnimatePresence = null;
try {
  const framerMotion = require("framer-motion");
  motion = framerMotion.motion;
  AnimatePresence = framerMotion.AnimatePresence;
} catch (e) {
  logger.warn("Framer Motion not available, using fallback components");
  // Fallback components if framer-motion is not available
  motion = {
    div: "div",
  };
  AnimatePresence = ({ children }) => <>{children}</>;
}

// Import icons (with fallbacks if not available)
let FolderIcon, BanknotesIcon, FireIcon, XMarkIcon, ChevronRightIcon;
try {
  const heroIcons = require("@heroicons/react/24/outline");
  const solidIcons = require("@heroicons/react/24/solid");

  FolderIcon = heroIcons.FolderIcon || (() => <span className="icon">📁</span>);
  BanknotesIcon =
    heroIcons.BanknotesIcon || (() => <span className="icon">💰</span>);
  FireIcon = heroIcons.FireIcon || (() => <span className="icon">🔥</span>);
  XMarkIcon = heroIcons.XMarkIcon || (() => <span className="icon">✖️</span>);
  ChevronRightIcon =
    solidIcons.ChevronRightIcon || (() => <span className="icon">▶️</span>);
} catch (e) {
  logger.warn("Hero Icons not available, using fallback icons");
  // Fallback icons
  FolderIcon = () => <span className="icon">📁</span>;
  BanknotesIcon = () => <span className="icon">💰</span>;
  FireIcon = () => <span className="icon">🔥</span>;
  XMarkIcon = () => <span className="icon">✖️</span>;
  ChevronRightIcon = () => <span className="icon">▶️</span>;
}

// Add TargetIcon fallback
let TargetIcon;
try {
  const heroIcons = require("@heroicons/react/24/outline");
  TargetIcon =
    heroIcons.BullseyeIcon || (() => <span className="icon">🎯</span>);
} catch (e) {
  TargetIcon = () => <span className="icon">🎯</span>;
}

// Simple Net Worth Card Component
const NetWorthCard = ({
  assetsTotal = 0,
  liabilitiesTotal = 0,
  assetBreakdown = {},
}) => {
  const netWorth = assetsTotal - liabilitiesTotal;
  const netWorthColor = netWorth >= 0 ? "text-green-600" : "text-red-600";
  const percentDebt =
    assetsTotal > 0 ? (liabilitiesTotal / assetsTotal) * 100 : 0;
  const debtToAssetRatio = assetsTotal > 0 ? liabilitiesTotal / assetsTotal : 0;
  // Placeholder for YoY Net Worth Change (should be replaced with real data if available)
  const yoyNetWorthChange = null; // e.g., { value: 0.05, isPositive: true }
  const COLORS = ["#4F8EF7", "#34C759", "#FFD600", "#FF9500"];
  const assetPieData = [
    {
      name: "Tax-Deferred (Traditional)",
      value: assetBreakdown.traditional || 0,
    },
    { name: "Tax-Free (Roth)", value: assetBreakdown.roth || 0 },
    {
      name: "Cash & Equivalents (Non-Qualified)",
      value: assetBreakdown.nonQualified || 0,
    },
    { name: "Property (Non-Qualified)", value: assetBreakdown.home || 0 },
  ];
  logger.info("Net Worth Card assetsData.assets:", assetsTotal);
  logger.info("Net Worth Card primaryResidenceValueData:", liabilitiesTotal);
  logger.info("Net Worth Card calculatedTotalAssets:", assetsTotal);
  return (
    <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-md dark:shadow-none p-6 min-h-[420px] flex flex-col justify-between relative">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Net Worth</h2>
      {/* Icon Stats Row */}
      <div className="flex flex-row gap-6 mb-6">
        {/* YoY Net Worth Change */}
        <div className="flex flex-col items-center flex-1 min-w-0">
          <div className="w-8 h-8 flex items-center justify-center mb-1">
            {yoyNetWorthChange ? (
              yoyNetWorthChange.isPositive ? (
                <ArrowTrendingUpIcon className="w-8 h-8 text-green-500" />
              ) : (
                <ArrowTrendingDownIcon className="w-8 h-8 text-red-500" />
              )
            ) : (
              <ScaleIcon className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-500">YoY Net Worth</span>
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {yoyNetWorthChange
              ? `${(yoyNetWorthChange.value * 100).toFixed(1)}%`
              : "--"}
          </span>
        </div>
        {/* Debt to Asset Ratio */}
        <div className="flex flex-col items-center flex-1 min-w-0">
          <div className="w-8 h-8 flex items-center justify-center mb-1">
            <ScaleIcon className="w-8 h-8 text-blue-500" />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-500">Debt to Asset Ratio</span>
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {assetsTotal > 0 ? `${(debtToAssetRatio * 100).toFixed(1)}%` : "--"}
          </span>
        </div>
      </div>
      {/* Pie Chart Row */}
      <div className="flex flex-col items-center justify-center mb-6">
        <div className="w-[220px] h-[220px] flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={assetPieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {assetPieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) =>
                  `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                }
              />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Net Worth and Breakdown */}
      <div className="grid grid-cols-3 gap-2 w-full mb-8">
        <div className="text-center">
          <div className="text-sm text-gray-500 dark:text-gray-500">Assets</div>
          <div className="text-lg font-semibold text-green-600">
            {formatCurrency(assetsTotal)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-500 dark:text-gray-500">Debt Ratio</div>
          <div className="text-lg font-semibold">
            {percentDebt > 0 ? percentDebt.toFixed(1) : 0}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-500 dark:text-gray-500">Liabilities</div>
          <div className="text-lg font-semibold text-red-600">
            {formatCurrency(liabilitiesTotal)}
          </div>
        </div>
      </div>
      {/* Net Worth bottom right */}
      <div className="flex justify-end items-end mt-auto">
        <span
          className={`font-bold text-lg ${netWorth < 0 ? "text-red-600" : "text-green-600"}`}
        >
          Net Worth: {formatCurrency(netWorth)}
        </span>
      </div>
    </div>
  );
};

const HouseholdDebtAnalysis = ({
  debtsData = [],
  members = [],
  assetsData = {
    assets: [],
    assetTypes: [],
    taxQualifications: [],
    accountTypes: [],
    financialInstitutions: [],
    properties: [],
    members: [],
    ownerTypes: [],
  },
  primaryResidenceValueData = 0,
  onSave,
  onSaveAssets,
}) => {
  const { isDarkMode } = useCustomTheme();

  // Debug log and defensive check
  logger.info(
    "HouseholdDebtAnalysis received assetsData.assets:",
    assetsData.assets,
    Array.isArray(assetsData.assets),
  );
  if (!Array.isArray(assetsData.assets)) {
    throw new Error(
      "assetsData.assets is not an array! Value: " +
        JSON.stringify(assetsData.assets),
    );
  }

  // Track loading/error state
  const [error, setError] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [highRateDebts, setHighRateDebts] = useState([]);
  const [filteredDebts, setFilteredDebts] = useState(debtsData);
  const [showHighRateOnly, setShowHighRateOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("remainingBalance");
  const [sortDirection, setSortDirection] = useState("desc");

  // Process debts data
  useEffect(() => {
    if (debtsData?.length > 0) {
      // Filter out invalid entries first
      const validDebts = debtsData.filter(debt => {
        const hasValidId = debt.client_debt_id || debt.debt_id;
        const hasValidPayments = debt.minimum_payment && debt.actual_payment;
        const hasValidType = debt.debt_type_id;
        return hasValidId && hasValidPayments && hasValidType;
      });

      // Ensure we have unique entries based on client_debt_id or debt_id
      const uniqueDebts = Array.from(
        new Map(validDebts.map(debt => [debt.client_debt_id || debt.debt_id, debt])).values()
      );

      // Find high interest rate debts (interest rate > 8%)
      const highInterestDebts = uniqueDebts.filter(
        (debt) => parseFloat(debt.interest_rate || 0) > 8,
      );
      setHighRateDebts(highInterestDebts);

      // Set the filtered debts
      handleFilterSort(
        uniqueDebts,
        showHighRateOnly,
        searchTerm,
        sortBy,
        sortDirection,
      );
    }
  }, [debtsData]);

  // Filter and sort the debts
  const handleFilterSort = (
    debts = debtsData,
    highRateOnly = showHighRateOnly,
    search = searchTerm,
    sort = sortBy,
    direction = sortDirection,
  ) => {
    // Start with valid, unique entries
    const validDebts = debts.filter(debt => {
      const hasValidId = debt.client_debt_id || debt.debt_id;
      const hasValidPayments = debt.minimum_payment && debt.actual_payment;
      const hasValidType = debt.debt_type_id;
      return hasValidId && hasValidPayments && hasValidType;
    });

    const uniqueDebts = Array.from(
      new Map(validDebts.map(debt => [debt.client_debt_id || debt.debt_id, debt])).values()
    );

    let filtered = [...uniqueDebts];

    // Apply high rate filter
    if (highRateOnly) {
      filtered = filtered.filter(
        (debt) => parseFloat(debt.interest_rate || 0) > 8,
      );
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((debt) =>
        (debt.debtor_name || "").toLowerCase().includes(searchLower),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sort) {
        case "interestRate":
          aValue = parseFloat(a.interest_rate || 0);
          bValue = parseFloat(b.interest_rate || 0);
          break;
        case "payoffDate":
          aValue = new Date(a.payoff_date || "2099-12-31");
          bValue = new Date(b.payoff_date || "2099-12-31");
          break;
        case "minimumPayment":
          aValue = parseFloat(String(a.minimum_payment || 0).replace(/[$,]/g, ""));
          bValue = parseFloat(String(b.minimum_payment || 0).replace(/[$,]/g, ""));
          break;
        case "remainingBalance":
        default:
          aValue = parseFloat(String(a.remaining_balance || 0).replace(/[$,]/g, ""));
          bValue = parseFloat(String(b.remaining_balance || 0).replace(/[$,]/g, ""));
          break;
      }

      return direction === "asc" ? aValue - bValue : bValue - aValue;
    });

    setFilteredDebts(filtered);
  };

  // Update filters and sorting
  useEffect(() => {
    handleFilterSort();
  }, [showHighRateOnly, searchTerm, sortBy, sortDirection]);

  // Handle saving data from the shared component
  const handleSaveDebts = (updatedDebts) => {
    try {
      // Pass the updated data back to the parent component
      if (onSave) {
        onSave(updatedDebts);
      }
    } catch (err) {
      logger.error("Error saving debt data:", err);
      setError("Error saving debt information");
    }
  };

  // Calculate the percentage of debts paid off
  const calculatePercentagePaidOff = () => {
    if (!debtsData || debtsData.length === 0) return 0;

    let totalOriginalBalance = 0;
    let totalRemainingBalance = 0;

    debtsData.forEach((debt) => {
      // Estimate original balance by adding remaining balance plus an estimated amount paid
      // Since we don't have original balance, we'll use a heuristic
      const remaining = parseFloat(
        String(debt.remaining_balance || debt.remainingBalance || 0).replace(
          /[$,]/g,
          "",
        ),
      );

      // Simple estimation - remaining balance is approximate % of original
      // Will be overridden if we get actual original balances in the future
      totalRemainingBalance += remaining;
      totalOriginalBalance += remaining * 1.2; // Assuming approximately 20% paid off on average if no better data
    });

    if (totalOriginalBalance === 0) return 0;
    const percentagePaidOff =
      ((totalOriginalBalance - totalRemainingBalance) / totalOriginalBalance) *
      100;
    return Math.round(percentagePaidOff);
  };

  // Calculate total liabilities
  const calculateTotalLiabilities = () => {
    return debtsData
      .filter((debt) => !debt.deleted)
      .reduce((total, debt) => {
        const amount = parseFloat(
          String(debt.remaining_balance || debt.remainingBalance || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        return total + (amount || 0);
      }, 0);
  };

  // Calculate total minimum payments
  const calculateTotalMinimumPayments = () => {
    return debtsData
      .filter((debt) => !debt.deleted)
      .reduce((total, debt) => {
        const amount = parseFloat(
          String(debt.minimum_payment || debt.minimumPayment || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        return total + (amount || 0);
      }, 0);
  };

  // Calculate total assets
  const calculateTotalAssets = () => {
    let total = 0;
    if (assetsData.assets && Array.isArray(assetsData.assets)) {
      total = assetsData.assets
        .filter((asset) => !asset.deleted)
        .reduce((sum, asset) => {
          const amount = parseFloat(
            String(asset.balance || asset.approximate_value || 0).replace(
              /[$,]/g,
              "",
            ),
          );
          return sum + (amount || 0);
        }, 0);
    }
    // Add the primary residence value if not already included in the assets array
    const hasPrimaryResidence =
      assetsData.assets &&
      assetsData.assets.some(
        (asset) =>
          asset.isPrimaryResidence ||
          (asset.assetTypeName &&
            asset.assetTypeName.toLowerCase().includes("residence")),
      );
    if (
      !hasPrimaryResidence &&
      parseFloat(primaryResidenceValueData || 0) > 0
    ) {
      total += parseFloat(primaryResidenceValueData);
    }
    return total;
  };

  // Format the total liabilities for display
  const totalLiabilities = formatCurrency(calculateTotalLiabilities());
  const totalAssets = calculateTotalAssets();

  // Get the percentage paid off
  const percentagePaidOff = calculatePercentagePaidOff();

  // Calculate monthly payments
  const monthlyPayments = formatCurrency(calculateTotalMinimumPayments());

  // Get number of accounts
  const accountCount = debtsData.filter((debt) => !debt.deleted).length;

  // Get high interest debts count and percentage
  const highRateCount = highRateDebts.length;
  const highRatePercentage =
    accountCount > 0 ? Math.round((highRateCount / accountCount) * 100) : 0;

  // Calculate the angle for the gauge needle
  const gaugeAngle = Math.min(percentagePaidOff, 100) * 1.8; // 180 degrees total range

  // Create a safe component that will never return undefined
  const SafeMotionDiv = motion?.div ? motion.div : "div";

  // Handle saving assets data
  const handleAssetsSave = (updatedAssetsData) => {
    if (onSaveAssets) {
      onSaveAssets(updatedAssetsData);
    }
  };

  // --- Utility functions for new calculations ---
  // Sum of high-rate remaining balances
  const getHighRateBalance = () => {
    return debtsData
      .filter(
        (debt) =>
          !debt.deleted &&
          parseFloat(debt.interest_rate || debt.interestRate || 0) > 8,
      )
      .reduce((total, debt) => {
        const amount = parseFloat(
          String(debt.remaining_balance || debt.remainingBalance || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        return total + (amount || 0);
      }, 0);
  };
  // Sum of all remaining balances
  const getTotalLiabilities = () => {
    return debtsData
      .filter((debt) => !debt.deleted)
      .reduce((total, debt) => {
        const amount = parseFloat(
          String(debt.remaining_balance || debt.remainingBalance || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        return total + (amount || 0);
      }, 0);
  };
  // Sum of all actual payments
  const getTotalActualPayments = () => {
    return debtsData
      .filter((debt) => !debt.deleted)
      .reduce((total, debt) => {
        const amount = parseFloat(
          String(debt.actual_payment || debt.actualPayment || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        return total + (amount || 0);
      }, 0);
  };
  // Projected payoff calculation
  const totalLiabilitiesNum = getTotalLiabilities();
  const highRateBalance = getHighRateBalance();
  const highRatePct =
    totalLiabilitiesNum > 0
      ? Math.round((highRateBalance / totalLiabilitiesNum) * 100)
      : 0;
  const totalActualPayments = getTotalActualPayments();

  // Calculate individual debt payoff dates and overall debt-free date
  const calculatePayoffDate = (balance, payment, rate) => {
    if (!payment || payment <= 0 || !balance || balance <= 0) return null;

    // Convert annual rate to monthly
    const monthlyRate = rate / 100 / 12;

    // If no interest rate, simple division
    if (!rate || rate <= 0) {
      const months = Math.ceil(balance / payment);
      return Math.min(months, 480); // Cap at 40 years
    }

    // Calculate minimum payment needed to cover interest
    const minPaymentNeeded = balance * monthlyRate;

    // If payment doesn't cover interest, debt will never be paid off
    if (payment <= minPaymentNeeded) {
      return 480; // Cap at 40 years
    }

    try {
      // Use amortization formula: n = -log(1 - (r*PV)/PMT) / log(1 + r)
      // where n is number of months, r is monthly rate, PV is balance, PMT is payment
      const months = Math.ceil(
        -Math.log(1 - (monthlyRate * balance) / payment) /
          Math.log(1 + monthlyRate),
      );

      // Cap at 40 years (480 months) for reasonable display
      return Math.min(months, 480);
    } catch (error) {
      logger.error("Error calculating payoff months:", error);
      return 480; // Default to 40 years on error
    }
  };

  // Calculate payoff dates for each debt
  const debtPayoffMonths = debtsData
    .filter((debt) => !debt.deleted)
    .map((debt) => {
      const balance = parseFloat(
        String(debt.remaining_balance || debt.remainingBalance || 0).replace(
          /[$,]/g,
          "",
        ),
      );
      const actualPayment = parseFloat(
        String(debt.actual_payment || debt.actualPayment || 0).replace(
          /[$,]/g,
          "",
        ),
      );
      const minPayment = parseFloat(
        String(debt.minimum_payment || debt.minimumPayment || 0).replace(
          /[$,]/g,
          "",
        ),
      );
      const payment = actualPayment > 0 ? actualPayment : minPayment;
      const rate = parseFloat(debt.interest_rate || debt.interestRate || 0);

      return {
        debt: debt,
        months: calculatePayoffDate(balance, payment, rate),
      };
    })
    .filter((result) => result.months !== null)
    .sort((a, b) => b.months - a.months);

  // Get the latest payoff date
  const latestPayoffMonths =
    debtPayoffMonths.length > 0 ? debtPayoffMonths[0].months : 0;
  const today = new Date();
  const payoffDate = new Date(
    today.getFullYear(),
    today.getMonth() + latestPayoffMonths,
    1,
  );

  // Format the date for display
  const formattedPayoffDate = payoffDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });

  // Get the maximum months to payoff (latest debt to be paid)
  const monthsToPayoff = latestPayoffMonths;

  // Compute debt-free date based on the latest payoff
  const debtFreeDate = monthsToPayoff ? formattedPayoffDate : null;

  // Format months as years+months
  const formatMonths = (months) => {
    if (!months) return "-";
    const y = Math.floor(months / 12);
    const m = months % 12;
    return y > 0 ? `${y} yr${y > 1 ? "s" : ""} ${m} mo` : `${m} mo`;
  };

  // --- Debt Ratio Calculation ---
  const debtRatio =
    totalAssets > 0 ? Math.round((totalLiabilitiesNum / totalAssets) * 100) : 0;

  // --- Gauge color utility for Debt Ratio ---
  const getDebtRatioColor = (ratio) => {
    if (ratio > 15) return "#FF3B30"; // Red
    if (ratio > 12) return "#FF9500"; // Orange
    if (ratio > 8) return "#FFCC00"; // Yellow-Orange
    if (ratio > 5) return "#FFD600"; // Yellow
    return "#34C759"; // Green
  };

  // --- Weighted Average Interest Rate Calculation ---
  const getWeightedAvgInterest = () => {
    let totalWeighted = 0;
    let totalBalance = 0;
    debtsData
      .filter((debt) => !debt.deleted)
      .forEach((debt) => {
        const balance = parseFloat(
          String(debt.remaining_balance || debt.remainingBalance || 0).replace(
            /[$,]/g,
            "",
          ),
        );
        const rate = parseFloat(debt.interest_rate || debt.interestRate || 0);
        totalWeighted += balance * rate;
        totalBalance += balance;
      });
    return totalBalance > 0 ? +(totalWeighted / totalBalance).toFixed(2) : 0;
  };
  const avgInterest = getWeightedAvgInterest();

  // --- Gauge SVG component for Average Interest ---
  const InterestGauge = ({ value }) => {
    // Clamp value to 0-20%
    const min = 0,
      max = 20;
    const pct = Math.max(min, Math.min(max, value));
    // Needle angle: 0% = 0deg (right, green), 20% = -180deg (left, red)
    const angle = 0 - ((pct - min) / (max - min)) * 180;
    // Define gradient colors with all transitions
    const gradientStops = [
      { offset: "0%", color: "#FF3B30" },    // Red (POOR)
      { offset: "16.6%", color: "#FF6A00" },  // Red-Orange
      { offset: "33.2%", color: "#FF9500" },  // Orange
      { offset: "49.8%", color: "#FFAA00" },  // Orange-Yellow
      { offset: "66.4%", color: "#FFCC00" },  // Yellow
      { offset: "83.2%", color: "#9ACD32" },  // Yellow-Green
      { offset: "100%", color: "#34C759" }    // Green (EXCELLENT)
    ];

    // Get current color based on angle for the value display
    const getCurrentColor = () => {
      // Convert angle to positive percentage (0 to 1) where 0 is red (left) and 1 is green (right)
      const percentage = 1 - (-angle / 180);
      
      // Find the appropriate gradient stop based on percentage
      for (let i = 0; i < gradientStops.length - 1; i++) {
        const currentStop = parseFloat(gradientStops[i].offset) / 100;
        const nextStop = parseFloat(gradientStops[i + 1].offset) / 100;
        
        if (percentage >= currentStop && percentage <= nextStop) {
          return gradientStops[i + 1].color;
        }
      }
      
      // Fallback to end colors if outside range
      return percentage <= 0 ? gradientStops[0].color : gradientStops[gradientStops.length - 1].color;
    };

    // Helper function to convert hex to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    // Arc generator for the gauge
    const describeArc = (start, end, radius = 32) => {  // Reduced radius from 48
      const startAngle = (Math.PI * start) / 180;
      const endAngle = (Math.PI * end) / 180;
      const x1 = 40 + radius * Math.cos(startAngle);  // Reduced from 80
      const y1 = 40 + radius * Math.sin(startAngle);  // Reduced from 80
      const x2 = 40 + radius * Math.cos(endAngle);    // Reduced from 80
      const y2 = 40 + radius * Math.sin(endAngle);    // Reduced from 80
      const largeArc = end - start > 180 ? 1 : 0;
      return `M${x1},${y1} A${radius},${radius} 0 ${largeArc} 1 ${x2},${y2}`;
    };



    return (
      <div className="flex flex-col items-center">
        <svg
          width="80"   // Reduced from 160
          height="60"  // Reduced from 120
          viewBox="0 0 80 60"  // Updated viewBox
          style={{ maxWidth: "100%" }}
        >
          {/* Define the gradient */}
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              {gradientStops.map((stop, i) => (
                <stop key={i} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>
          
          {/* Single gradient arc - adjusted coordinates */}
          <path
            d={describeArc(-180, 0, 32)}  // Reduced radius
            stroke="url(#gaugeGradient)"
            strokeWidth={12}  // Reduced from 20
            fill="none"
            strokeLinecap="round"
          />
          {/* Needle - adjusted coordinates */}
          <g>
            <line
              x1="40"  // Adjusted from 80
              y1="40"  // Adjusted from 80
              x2={40 + 32 * Math.cos((Math.PI * angle) / 180)}  // Adjusted coordinates
              y2={40 + 32 * Math.sin((Math.PI * angle) / 180)}  // Adjusted coordinates
              stroke="#222"
              strokeWidth={2}  // Reduced from 4
              strokeLinecap="round"
            />
            <circle cx="40" cy="40" r="4" fill="#222" />  // Reduced from r="8"
          </g>
          {/* Labels - adjusted coordinates and sizes */}
          <text x="6" y="55" fontSize="8" fill="#FF3B30" fontWeight="bold">  {/* Reduced font size and adjusted y */}
            POOR
          </text>
          <text
            x="74"  // Adjusted from 148
            y="55"  // Adjusted from 110
            fontSize="8"  // Reduced from 12
            fill="#34C759"
            fontWeight="bold"
            textAnchor="end"
          >
            EXCELLENT
          </text>
        </svg>
        <span className="text-[10px] text-white tracking-wide uppercase mt-0.5">
          Avg. Interest Rate:{" "}
          <span className="font-bold text-xs align-middle" style={{ color: getCurrentColor() }}>
            {value}%
          </span>
        </span>
      </div>
    );
  };

  useEffect(() => {
    logger.info("Net Worth Card assetsData.assets:", assetsData.assets);
    logger.info(
      "Net Worth Card primaryResidenceValueData:",
      primaryResidenceValueData,
    );
    logger.info(
      "Net Worth Card calculatedTotalAssets:",
      calculateTotalAssets(),
    );
    // eslint-disable-next-line
  }, []); // Only run once on mount

  // Get the current color based on average interest rate
  const getCurrentInterestColor = () => {
    if (avgInterest > 15) return "#FF3B30"; // Red
    if (avgInterest > 12) return "#FF9500"; // Orange
    if (avgInterest > 8) return "#FFCC00"; // Yellow-Orange
    if (avgInterest > 5) return "#FFD600"; // Yellow
    return "#34C759"; // Green
  };

  return (
    <div 
      className="p-4 space-y-4 relative group cursor-pointer transition-all"
      onClick={() => setShowDetailsDrawer(true)}
    >
      {/* Top-right tooltip */}
      <div
        className={`absolute -top-2 -right-2 rounded-lg px-3 py-1.5 text-xs font-medium
                   text-white opacity-0 group-hover:opacity-100
                   transform translate-y-1 group-hover:translate-y-0
                   transition-all z-50`}
        style={{ backgroundColor: getCurrentInterestColor() }}
      >
        View/Edit Debt Details
        <div 
          className="absolute -bottom-1 right-3 w-2 h-2 transform rotate-45"
          style={{ backgroundColor: getCurrentInterestColor() }}
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Liabilities
        </h2>
      </div>

      {/* Average Interest Gauge */}
      <div className="flex flex-col items-center mb-1">
        <div className="max-w-[80px] mx-auto">
          <InterestGauge value={avgInterest} />
        </div>
      </div>

      {/* Stats row */}
      <div className="w-full mx-auto flex justify-evenly gap-x-1 mt-1 items-center">
        <div className="flex-1 min-w-0 flex flex-col items-center">
          <div className="w-3 h-3 flex justify-center mb-0.5">
            <span className="text-base" role="img" aria-label="Accounts">
              📁
            </span>
          </div>
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>Accounts</span>
          <span className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
            {accountCount}
          </span>
        </div>
        <div className="flex-1 min-w-0 flex flex-col items-center">
          <div className="w-3 h-3 flex justify-center mb-0.5">
            <span className="text-base" role="img" aria-label="Monthly Payment">
              💰
            </span>
          </div>
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>Monthly Pmt</span>
          <span className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
            {formatCurrency(totalActualPayments)}
          </span>
        </div>
        <div className="flex-1 min-w-0 flex flex-col items-center">
          <div className="w-3 h-3 flex justify-center mb-0.5">
            <span className="text-base" role="img" aria-label="High Rate">
              🔥
            </span>
          </div>
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>High Rate</span>
          <span className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
            {highRateCount}{" "}
            <span className="text-xs text-red-600">({highRatePct}%)</span>
          </span>
        </div>
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center h-full">
          <div className="w-3 h-3 flex justify-center mb-0.5">
            <span className="text-base flex items-center justify-center h-full" role="img" aria-label="Debt Free">
              🎯
            </span>
          </div>
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>Debt Free</span>
          <span className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
            {monthsToPayoff ? debtFreeDate : "—"}
          </span>
        </div>
      </div>

      {/* Total row */}
      <div className="mt-1 flex justify-end">
        <span className={`font-semibold text-xs ${isDarkMode ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
          Total Liabilities: {formatCurrency(totalLiabilitiesNum)}
        </span>
      </div>

      {/* Drawer */}
      {showDetailsDrawer && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setShowDetailsDrawer(false)}
          />

          {/* Drawer Panel */}
          <div
            className={`fixed inset-y-0 right-0 w-full md:w-3/5 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl z-50 overflow-auto`}
            style={{
              transform: "translateX(0%)",
              transition: "transform 0.4s ease-in-out",
            }}
          >
            {/* Drawer Header */}
            <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} px-6 py-4 flex justify-between items-center`}>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Liabilities Details
              </h2>
              <button
                onClick={() => setShowDetailsDrawer(false)}
                className={`${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-800'} focus:outline-none`}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Filters Toolbar */}
            <div className={`flex items-center gap-4 py-3 px-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center">
                <label
                  htmlFor="high-rate-toggle"
                  className={`mr-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Show high-rate only
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="high-rate-toggle"
                    checked={showHighRateOnly}
                    onChange={() => {
                      setShowHighRateOnly(!showHighRateOnly);
                    }}
                    className="sr-only"
                  />
                  <div
                    className={`block w-10 h-6 rounded-full ${showHighRateOnly ? "bg-blue-500" : isDarkMode ? "bg-gray-600" : "bg-gray-300"} transition`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${showHighRateOnly ? "translate-x-4" : ""}`}
                  ></div>
                </div>
              </div>
              <div className="flex items-center">
                <label
                  htmlFor="sort-select"
                  className={`mr-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Sort by
                </label>
                <select
                  id="sort-select"
                  value={`${sortBy}-${sortDirection}`}
                  onChange={(e) => {
                    const [newSortBy, newDirection] = e.target.value.split("-");
                    setSortBy(newSortBy);
                    setSortDirection(newDirection);
                  }}
                  className={`px-3 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'border-gray-300 text-gray-700'
                  }`}
                >
                  <option value="remainingBalance-desc">Balance ↓</option>
                  <option value="remainingBalance-asc">Balance ↑</option>
                  <option value="interestRate-desc">Interest Rate ↓</option>
                  <option value="interestRate-asc">Interest Rate ↑</option>
                  <option value="payoffDate-asc">Payoff Date ↑</option>
                  <option value="payoffDate-desc">Payoff Date ↓</option>
                  <option value="minimumPayment-desc">Payment ↓</option>
                  <option value="minimumPayment-asc">Payment ↑</option>
                </select>
              </div>
            </div>

            {/* Debt Table */}
            <div
              className="px-6 py-4 overflow-auto"
              style={{ height: "calc(100vh - 250px)" }}
            >
              <DebtAnalysisTable
                debtsData={filteredDebts}
                members={members}
                onSave={handleSaveDebts}
                config={{
                  title: "",
                  editable: true,
                  showDates: true,
                  showActualPayment: true,
                  showDebtorName: true,
                  showPayoffDate: true,
                  showLastUpdated: true,
                  showMember: true,
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HouseholdDebtAnalysis;
